<template>

  <div id="menu-photo-importer" class="ai-importer" v-if="user_confirmed">

    <div class="menu-name" @click="redirectToMenu()">{{ menu.name }}</div>

    <h2>Import from photo or PDF</h2>

    <ErrorNotice v-if="error != ''" :message="error" :action-label="errorAction()" @action="runErrorAction()" />

    <p class="explainer">Upload photos or a PDF of your printed menu. We'll detect the categories, items and prices so you can review them before they're added to your menu.</p>

    <!-- step 1: choose files -->
    <div v-if="entries.length === 0">

      <MenuFilePicker v-model="files" :disabled="extracting" @error="error = $event" />

      <label class="apply-styles import-styles-option" v-if="!extracting && !has_items">
        <input type="checkbox" v-model="import_styles" />
        <span><strong>Import styles</strong>: also read the menu's fonts, colours, sizes and dividers from its first few items, and apply them to every item on this menu. You'll see a preview before anything is saved.</span>
      </label>

      <button v-if="files.length > 0 && !extracting" @click="extract()">Detect items</button>

      <div class="status" v-if="extracting">
        <div class="spinner"></div>
        <p>Reading your menu… this can take a minute or two for longer menus.</p>
      </div>
      <p class="navigate-away-note" v-if="extracting">Feel free to navigate away, you can monitor all menu auto-import and translation jobs here: <router-link to="/ai_jobs">Auto-import and translation jobs</router-link></p>

    </div>


    <p class="ai-cost" v-if="isAdmin() && cost && cost.requests > 0">AI cost: {{ formatAiCost(cost) }}</p>

    <!-- step 2: review -->
    <div class="review" v-if="entries.length > 0">

      <div class="review-header">
        <p class="review-title">{{ entries.length }} entries detected</p>
        <p class="review-subtitle">Check names and prices, untick anything you don't want, then add them to your menu. Items are added after your existing items.</p>
        <p class="start-over" @click="startOver()">Start over with different files</p>
      </div>

      <!-- currency read from the upload, for a menu that doesn't have one yet -->
      <div class="currency-choice" v-if="currency_options">
        <label for="menu-currency"><strong>Currency</strong></label>
        <select id="menu-currency" v-model="currency">
          <option value="">Not specified</option>
          <option v-for="option in currency_options" :key="option" :value="option">{{ option }}</option>
        </select>
        <span class="currency-note">{{ detected_currency ? 'Detected from your menu' : "We couldn't tell from your menu; choose one if you like" }}</span>
      </div>

      <!-- detected styles (only for an import into an empty menu) -->
      <div class="detected-styles" v-if="styles">

        <label class="apply-styles">
          <input type="checkbox" v-model="apply_styles" />
          <span><strong>Import styles</strong>: apply these detected styles to every item on this menu and turn custom styles on</span>
        </label>

        <div class="styles-preview" :class="{ 'entry-excluded': !apply_styles }">
          <div v-for="sample in styleSamples()" :key="sample.kind" class="preview-entry" :style="previewStyle(sample.kind, 'item_css')"
            :class="{ 'preview-price-left': sample.price && pricePosition() === 'left' }">
            <p v-if="sample.price && pricePosition() === 'left'" class="preview-price" :style="previewStyle(sample.kind, 'item_name_css')">{{ sample.price }}</p>
            <div class="preview-text">
              <p :style="previewStyle(sample.kind, 'item_name_css')">{{ sample.name }}</p>
              <p v-if="sample.description" :style="previewStyle(sample.kind, 'item_description_css')">{{ sample.description }}</p>
              <p v-if="sample.price && pricePosition() !== 'left'" class="preview-price" :style="previewStyle(sample.kind, 'item_name_css')"
                :class="{ 'preview-price-middle': pricePosition() === 'middle', 'preview-price-right': pricePosition() !== 'middle' }">{{ sample.price }}</p>
              <p class="font-match" v-if="fontMatches(sample.kind)">{{ fontMatches(sample.kind) }}</p>
            </div>
          </div>
        </div>

        <p class="styles-note">You can turn these styles off later, for single items or the whole menu, under "Alignment & bulk sorting" in the item options.</p>

      </div>

      <div class="entry" v-for="(entry, index) in entries" :key="index"
        :class="{ 'entry-category': entry.kind !== 'item', 'entry-excluded': !entry.included }">

        <input type="checkbox" v-model="entry.included" class="include-checkbox" />

        <div class="entry-fields">

          <div class="entry-top-row">

            <select v-model="entry.kind">
              <option value="major_category">Major category</option>
              <option value="category">Category</option>
              <option value="item">Item</option>
            </select>

            <input type="text" v-model="entry.name" class="name-input" placeholder="Name" />

          </div>

          <div v-if="entry.kind === 'item'">

            <textarea v-model="entry.description" placeholder="Description (optional)" rows="2"></textarea>

            <div class="prices">

              <div class="price" v-for="(price, price_index) in entry.prices" :key="price_index">
                <input type="number" step="any" min="0" v-model="price.amount" placeholder="Price" />
                <input type="text" v-model="price.label" placeholder="Unit (e.g. Glass)" />
                <img src="@/assets/delete_image.svg" @click="entry.prices.splice(price_index, 1)" />
              </div>

              <p class="add-price" v-if="entry.prices.length < 3" @click="entry.prices.push({ amount: '', label: '' })">+ Add price</p>

            </div>


          </div>

        </div>

      </div>

    </div>

    <div class="confirm-changes" v-if="entries.length > 0 && !creating" @click="createItems()" :class="{ 'confirm-disabled': includedEntries().length === 0 }">
      <img src="@/assets/checkmark_normal.svg" />
      <p>Add {{ includedEntries().length }} {{ includedEntries().length === 1 ? 'entry' : 'entries' }} to menu</p>
    </div>

    <div class="confirm-changes" v-if="creating">
      <p>Adding items…</p>
    </div>

  </div>

</template>


<script>
import ErrorNotice from './ErrorNotice'
import signedInOnly from './signedInOnly'
import { formatAiCost } from './aiCost'
import { runAiJob, pollAiJob, findOpenJob, rememberJob, forgetJob } from './aiJob'
import MenuFilePicker from './MenuFilePicker'
import { menuFilesFormData } from './menuFiles'
import { parseCss, loadFonts } from './menuCss'
import { takePendingImport } from './pendingImport'

export default {
  name: "MenuPhotoImporter",
  components: { ErrorNotice, MenuFilePicker },
  data() {
    return {
      menu: {},
      files: [],
      entries: [],
      extracting: false,
      creating: false,
      error: '',
      error_code: null,
      cost: null,
      styles: null,
      apply_styles: false,
      // "Import styles": ticked by default for an import into an empty menu
      import_styles: false,
      has_items: false,
      job_id: null, // the import run being shown (kept in the URL as ?job= so it can be found again)
      // currency read from the upload (only offered while the menu has none)
      currency_options: null,
      detected_currency: '',
      currency: ''
    }
  },
  mixins: [signedInOnly],
  methods: {
    // a button under the error, for the errors the user can do something about
    errorAction() {
      return { subscription_expired: 'See plans', ai_limit_reached: 'Contact us' }[this.error_code] || ''
    },
    runErrorAction() {
      this.$router.push(this.error_code === 'subscription_expired' ? '/plans' : '/contact')
    },
    onUserConfirmed() {
      this.$http.plain.get('/app_show_menu_v2/' + this.menuId())
        .then(response => {
          this.menu = response.data.menu
          this.has_items = (response.data.foods || []).length > 0
          // styles are only imported into a new (empty) menu, never onto existing items
          this.import_styles = !this.has_items
        })
      // An upload handed over from the create-menu page starts reading straight away.
      const upload = takePendingImport(this.menuId())
      if (upload) {
        this.files = upload.files
        this.import_styles = upload.importStyles
        this.extract()
      } else {
        this.resumeJob()
      }
    },
    // Picks up this menu's import run if there is one: from the URL, or the latest still running or not
    // yet reviewed. Shows the spinner while it runs and the review once it's ready.
    async resumeJob() {
      const open = this.$route.query.job ? null : await findOpenJob(this, this.menuId(), 'import')
      const jobId = this.$route.query.job || (open && open.job_id)
      if (!jobId) {
        return
      }
      this.job_id = jobId
      rememberJob(this, jobId)
      this.extracting = true
      try {
        const response = await pollAiJob(this, jobId)
        if (response.review_state !== 'pending') {
          // already added or discarded: start fresh
          this.job_id = null
          forgetJob(this)
          return
        }
        this.showExtraction(response.data)
      } catch (error) {
        this.setError(error, 'Something went wrong reading your menu. Please try again.')
      } finally {
        this.extracting = false
      }
    },
    formatAiCost,
    // AI costs are shown to admins only (the server leaves them out for everyone else too)
    isAdmin() {
      return this.$store.getters.getUserType === 'admin'
    },
    menuId() {
      return this.$router.currentRoute.params.menu_id
    },
    async extract() {
      this.error = ''
      this.extracting = true
      try {
        const formData = await menuFilesFormData(this.files)
        formData.append('import_styles', this.import_styles && !this.has_items)
        const response = await runAiJob(this, this.$http.upload.post(`/app_extract_menu_items/${this.menuId()}`, formData), {
          onStarted: jobId => {
            this.job_id = jobId
            rememberJob(this, jobId)
          }
        })
        this.showExtraction(response.data)
      } catch (error) {
        this.setError(error, 'Something went wrong reading your menu. Please try again.')
      } finally {
        this.extracting = false
      }
    },
    // Shows a finished import run's result for review.
    showExtraction(data) {
      this.cost = data.cost
      this.styles = data.styles || null
      this.currency_options = data.currency_options || null
      this.detected_currency = data.currency || ''
      this.currency = this.detected_currency
      this.apply_styles = this.styles !== null
      this.entries = data.entries.map(entry => ({
        ...entry,
        description: entry.description || '',
        prices: entry.prices.map(price => ({ amount: price.amount, label: price.label || '' })),
        included: true
      }))
      if (this.entries.length === 0) {
        this.error = "We couldn't find any menu items in those files. Try a clearer photo."
      }
    },
    includedEntries() {
      return this.entries.filter(entry => entry.included && entry.name.trim() !== '')
    },
    // One sample per detected kind (a major category, a category and an item), using names from this menu.
    styleSamples() {
      const labels = { major_category: 'Major category', category: 'Category', item: 'Item' }
      return ['major_category', 'category', 'item'].filter(kind => this.styles[kind]).map(kind => {
        const entry = this.entries.find(e => e.kind === kind)
        const price = entry && entry.prices && entry.prices.length > 0 ? entry.prices[0].amount : 12
        return {
          kind,
          name: entry ? entry.name : labels[kind],
          description: kind === 'item' ? ((entry && entry.description) || 'Item description') : '',
          price: kind === 'item' ? price : null
        }
      })
    },
    // "Name: Futura PT → Jost · Description: Lora": the font Claude identified on the menu and the font used.
    fontMatches(kind) {
      const fonts = this.styles[kind] && this.styles[kind].fonts
      if (!fonts) {
        return ''
      }
      const parts = [['name', 'Name'], ['description', 'Description']].filter(([part]) => fonts[part] && (part === 'name' || this.styles[kind].item_description_css))
        .map(([part, label]) => {
          const { identified, used } = fonts[part]
          const match = identified && used && identified.toLowerCase() !== used.toLowerCase() ? `${identified} → ${used}` : (used || identified || 'default font')
          return `${label}: ${match}`
        })
      return parts.join(' · ')
    },
    // Detected price position for items: "left", "middle" or "right" (right when none was detected).
    pricePosition() {
      return (this.styles.item && this.styles.item.price_position) || 'right'
    },
    previewStyle(kind, column) {
      const style = parseCss(this.styles[kind][column])
      loadFonts(style)
      return style
    },
    createItems() {
      const entries = this.includedEntries().map(entry => ({
        kind: entry.kind,
        name: entry.name,
        description: entry.description,
        prices: entry.kind === 'item' ? entry.prices.filter(price => price.amount !== '' && price.amount !== null) : []
      }))
      if (entries.length === 0) {
        return
      }
      this.error = ''
      this.creating = true
      const styles = this.styles && this.apply_styles ? this.styles : null
      const currency = this.currency_options && this.currency ? this.currency : null
      runAiJob(this, this.$http.secured.post(`/app_create_extracted_items/${this.menuId()}`, { entries, styles, currency, job_id: this.job_id }))
        .then(response => {
          this.$store.commit('setAnchoredItem', response.data.first_food_id)
          this.redirectToMenu()
        })
        .catch(error => {
          this.creating = false
          this.setError(error, "Couldn't add items. Please try again.")
        })
    },
    startOver() {
      if (confirm('Discard the detected items and start over?')) {
        if (this.job_id) {
          this.$http.secured.post(`/app_ai_jobs/${this.job_id}/dismiss`).catch(() => null)
          this.job_id = null
          forgetJob(this)
        }
        this.entries = []
        this.styles = null
        this.currency_options = null
        this.files = []
        this.error = ''
      }
    },
    redirectToMenu() {
      this.$router.replace(`/${this.menuId()}`)
    },
    setError(error, text) {
      const data = (error.response && error.response.data) || {}
      this.error = data.error || error.message || text
      this.error_code = data.code || null
      // A failed run can still have used (and been charged for) AI requests.
      if (data.cost) {
        this.cost = data.cost
      }
    }
  }
}
</script>

<style scoped src="./importer.css"></style>

<style scoped>

.currency-choice {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  margin-bottom: 20px;
}

.currency-choice select {
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
  padding: 5px 6px;
}

.currency-note {
  font-size: 12px;
  color: dimgray;
}

.detected-styles {
  margin-bottom: 30px;
  padding: 15px;
  border: 1px solid lightgray;
  background: white;
}

.apply-styles {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  font-size: 13px;
  cursor: pointer;
}

.apply-styles input {
  margin: 3px 10px 0px 0px;
  flex-shrink: 0;
}

.import-styles-option {
  margin: 20px 0px;
}

.styles-preview {
  margin-top: 15px;
}

.preview-entry {
  padding: 10px 15px;
}

.preview-entry p {
  margin: 0px 0px 4px 0px;
}

.preview-price-left {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
}

.preview-price-left .preview-price {
  flex-shrink: 0;
  min-width: 60px;
  padding-right: 20px;
}

.preview-text {
  flex: 1;
}

.font-match {
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  color: dimgray;
  margin-top: 6px !important;
}

.preview-price-middle {
  text-align: center;
}

.preview-price-right {
  text-align: right;
}

.styles-note {
  font-size: 12px;
  color: dimgray;
  margin: 15px 0px 0px 0px;
}

</style>
